// The design's step tracker: a row of steps with the reached ones filled in.
//
// It is a picture of a state the screen already states in words, never a source
// of its own. The caller says how many steps are EVIDENCED — a step is filled
// only when the record says it happened — and a state that leaves the happy
// path (outcome unknown, not applied, cancelled) gets no tracker at all rather
// than a row of steps that suggests where it is heading.
//
// Decorative, so aria-hidden, and the labels are drawn from `data-label` by the
// stylesheet rather than written into the DOM: the badge beside it is the one
// accessible statement of the state, and a second "Confirmed" in the text would
// be a second statement for someone to read out of context.

import type { ReactNode } from 'react';
import { Icon, type IconName } from './Icon.tsx';

export interface Step {
  readonly label: string;
  readonly icon: IconName;
}

export interface StepTrackerProps {
  readonly steps: readonly Step[];
  /** How many steps, from the first, the record shows as done. */
  readonly reached: number;
  /** True when the next step is under way rather than merely next. */
  readonly moving?: boolean;
}

export function StepTracker({ steps, reached, moving = false }: StepTrackerProps): ReactNode {
  return (
    <div className="steps" aria-hidden="true">
      {steps.map((step, index) => {
        const state = index < reached ? 'on' : moving && index === reached ? 'cur' : '';
        return (
          <div className="step-wrap" key={step.label}>
            {index === 0 ? null : (
              <div className="sline">
                <i style={{ width: index < reached ? '100%' : '0' }} />
              </div>
            )}
            <div className={`sp ${state}`.trim()}>
              <div className="sd">
                <Icon name={index < reached ? 'check' : step.icon} size={14} />
              </div>
              <div className="sl" data-label={step.label} />
            </div>
          </div>
        );
      })}
    </div>
  );
}
